import React, { useMemo, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { RegisterNewForm } from "../../components/RegisterNewForm/RegisterNewForm";
import { useEditNewPage } from "./useEditNewPage";
import { PageWrapper } from "../../../../shared/components/layout/PageWrapper";
import { ContextInfoCard } from "../../../../shared/components/ui/ContextInfoCard";
import { ROUTES } from "../../../../routes/routes";

/**
 * EditNewPage
 * Admin-only page to edit an existing news (novedad).
 * Loads the new by new_id from the route and preloads the form.
 * Shows context info (lifeguard name, building, city) when available.
 */
export const EditNewPage: React.FC = () => {
  const { newId } = useParams<{ newId: string }>();
  const {
    newItem,
    reportTypes,
    maintenanceTypes,
    poolRegulationTypes,
    interventionLevels,
    adverseEventTypes,
    initialData,
    handleFormSubmit,
    handleNavigateToDashboard,
    isSubmitting,
    isLoadingNew,
    isErrorNew,
    swimmer,
  } = useEditNewPage();

  // Ensure the page always opens scrolled to top (avoids restored scroll from nav/history)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [newId]);

  const contextItems = useMemo(
    () =>
      newItem
        ? [
            { label: "Salvavidas de turno", value: newItem.lifeguard_name },
            { label: "Unidad residencial", value: newItem.building_name },
            { label: "Ciudad", value: newItem.city },
          ]
        : [],
    [newItem]
  );

  if (!newId) {
    return <Navigate to={ROUTES.DASHBOARD.ADMINISTRATOR} replace />;
  }

  return (
    <PageWrapper
      isLoading={isLoadingNew}
      loadingLabel="Cargando novedad..."
      isError={isErrorNew}
      errorMessage="No se pudo cargar la novedad. Puede que no exista o no tenga permiso para editarla."
      onBack={handleNavigateToDashboard}
      title="Editar Novedad"
    >
      {newItem && initialData ? (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
          <ContextInfoCard
            title="Contexto de la novedad"
            items={contextItems}
          />
          <RegisterNewForm
            key={newId}
            onSubmit={handleFormSubmit}
            reportTypes={reportTypes}
            maintenanceTypes={maintenanceTypes}
            poolRegulations={poolRegulationTypes}
            interventionLevels={interventionLevels}
            adverseEventTypes={adverseEventTypes}
            initialData={initialData}
            isSubmitting={isSubmitting}
            submitLabel="Guardar cambios"
            swimmer={swimmer}
            allowDateEdit
          />
        </div>
      ) : null}
    </PageWrapper>
  );
};
